/* eslint-disable id-length */
import type { CSSProperties, JSX } from 'react';
import { useCallback, useMemo } from 'react';
import {
  Root as SliderRoot,
  Track as SliderTrack,
  Thumb as SliderThumb,
} from '@radix-ui/react-slider';
import { styled } from 'styled-components';
import { formatHex } from 'culori/fn';
import { useColorPickerState } from './ColorPickerContext';
import { THREE_SIXTY } from './consts';
import { HSVHueCanvas } from './HSVHueCanvas';

const TWENTY_FOUR = 24;

const Container = styled.div`
  border-radius: var(--wui-border-radius-rounded);
  border: 1px solid var(--wui-color-border);
  height: ${TWENTY_FOUR}px;
  overflow: hidden;
  padding: 0 var(--wui-space-03);
  position: relative;
`;

const Root = styled(SliderRoot)`
  align-items: center;
  display: flex;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: calc(100% - var(--wui-space-05));
  height: 16px;
`;

const Track = styled(SliderTrack)`
  width: 100%;
`;

const Thumb = styled(SliderThumb)`
  position: relative;
`;

const ThumbInner = styled.div`
  cursor: pointer;
  display: block;
  border-radius: var(--wui-border-radius-rounded);
  box-shadow:
    inset 0 0 0 1px var(--wui-color-border),
    inset 0 0 0 2px black;
  height: 16px;
  position: absolute;
  transform: translate(-50%, -50%);
  width: 16px;

  ${Thumb}:focus-visible > & {
    box-shadow:
      inset 0 0 0 2px var(--wui-color-border),
      inset 0 0 0 3px black;
  }
`;

export type HueSliderProps = object;

/**
 * A horizontal slider for adjusting the hue of the current color, rendered
 * over a spectrum gradient. The thumb color reflects the currently selected
 * hue, saturation, and value.
 */
export const HueSlider = (props: HueSliderProps): JSX.Element => {
  const { nonDerivedValueAsHsv, onChangeNonDerivedValueAsHsv, valueAsHsv } = useColorPickerState();

  // We have some background here that needs to match the color at either end of
  // the hue slider. It's so the thumb can be inset from the edge of the
  // gradient background when at the extremes of the slider, while still being
  // able to reach those extreme values.
  const containerStyle = useMemo<CSSProperties>(() => {
    return {
      backgroundColor: formatHex({
        h: 0, // red
        s: valueAsHsv.s,
        v: valueAsHsv.v,
        mode: 'hsv' as const,
      }),
    };
  }, [valueAsHsv.s, valueAsHsv.v]);

  const thumbInnerStyle = useMemo<CSSProperties>(() => {
    return { backgroundColor: formatHex(valueAsHsv) };
  }, [valueAsHsv]);

  const onHueChange = useCallback(
    (values: number[]) => {
      const newHue = values[0] ?? 0;

      onChangeNonDerivedValueAsHsv({
        ...nonDerivedValueAsHsv,
        h: newHue,
      });
    },
    [nonDerivedValueAsHsv, onChangeNonDerivedValueAsHsv],
  );

  return (
    <Container
      {...props}
      style={containerStyle}
    >
      <Root
        max={THREE_SIXTY}
        min={0}
        onValueChange={onHueChange}
        orientation="horizontal"
        step={1}
        value={[nonDerivedValueAsHsv.h ?? 0]}
      >
        <Track />
        <Thumb aria-label="Hue slider">
          <ThumbInner style={thumbInnerStyle} />
        </Thumb>
      </Root>
      <HSVHueCanvas hsv={valueAsHsv} />
    </Container>
  );
};

HueSlider.displayName = 'HueSlider';
